import { useEffect, useState } from 'react';
import Modal from './Modal';
import {
  AVAILABILITY_IMAGE_DEFAULT_SETTINGS,
  AVAILABILITY_IMAGE_TEXT_PX_MAX,
  AVAILABILITY_IMAGE_TEXT_PX_MIN,
} from '../constants/bookingConst';
import { validateImageSettings } from '../utils/availabilityImageSettingsUtil';
import '../css/AvailabilityImageSettingsModal.css';

// Font-size fields, in the order shown.
const FONT_FIELDS = [
  { key: 'timePx', label: 'Time labels', hint: 'e.g. "6:00 – 7:00 PM"' },
  { key: 'cellPx', label: 'Slot labels', hint: 'e.g. "OPEN", "BOOKED"' },
];

// BOOKED colour fields, in the order shown.
const BOOKED_FIELDS = [
  { key: 'bookedBg', label: 'Background' },
  { key: 'bookedText', label: 'Font color' },
];

/**
 * Turns stored settings into form values (px as strings, blank = automatic).
 * @param {object} settings - normalized settings
 * @returns {{timePx: string, cellPx: string, bookedBg: string, bookedText: string}}
 */
const toDraft = (settings) => ({
  timePx: settings.timePx == null ? '' : String(settings.timePx),
  cellPx: settings.cellPx == null ? '' : String(settings.cellPx),
  bookedBg: settings.bookedBg,
  bookedText: settings.bookedText,
});

/**
 * The gear dialog of Availability images: font sizes in px for the time
 * labels and slot labels, and the BOOKED cell colours. These only affect the
 * generated images. Apply hands the validated settings up (the parent saves
 * them and regenerates); × and Cancel discard the edits. Built on the shared
 * Modal, so a tap outside does nothing.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {object} props.settings - the settings currently applied
 * @param {(settings: object) => void} props.onApply - called with normalized settings
 * @param {() => void} props.onClose
 * @returns {JSX.Element|null}
 */
function AvailabilityImageSettingsModal({ isOpen, settings, onApply, onClose }) {
  const [draft, setDraft] = useState(() => toDraft(settings));
  const [errors, setErrors] = useState({});

  // Start from the applied settings each time it opens, so a cancelled edit
  // doesn't linger.
  useEffect(() => {
    if (isOpen) {
      setDraft(toDraft(settings));
      setErrors({});
    }
  }, [isOpen, settings]);

  if (!isOpen) return null;

  /**
   * Updates one form field and clears its error.
   * @param {string} key
   * @param {string} value
   * @returns {void}
   */
  const setField = (key, value) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: '' }));
  };

  /**
   * Validates the form and hands the settings up, or shows what's wrong.
   * @returns {void}
   */
  const handleApply = () => {
    const result = validateImageSettings(draft);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    onApply(result.settings);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Image settings"
      hideIcon
      showCancel
      confirmText="Apply & regenerate"
      onConfirm={handleApply}
      className="ais-modal"
    >
      <p className="ais-note">These settings only change the generated images, not the booking page.</p>

      <section className="ais-section">
        <h3 className="ais-section-title">Font size (px)</h3>
        <p className="ais-hint">
          Leave blank for automatic sizing. Sizes are in the full-size image
          ({AVAILABILITY_IMAGE_TEXT_PX_MIN}–{AVAILABILITY_IMAGE_TEXT_PX_MAX} px; a story image is 1080 px wide).
        </p>
        <div className="ais-fields">
          {FONT_FIELDS.map(field => (
            <label key={field.key} className="ais-field" htmlFor={`ais-${field.key}`}>
              <span className="ais-label">{field.label}</span>
              <div className="ais-px-input">
                <input
                  id={`ais-${field.key}`}
                  type="number"
                  inputMode="decimal"
                  min={AVAILABILITY_IMAGE_TEXT_PX_MIN}
                  max={AVAILABILITY_IMAGE_TEXT_PX_MAX}
                  step="1"
                  placeholder="Auto"
                  value={draft[field.key]}
                  className={errors[field.key] ? 'ais-input-error' : ''}
                  onChange={e => setField(field.key, e.target.value)}
                />
                <span className="ais-unit">px</span>
              </div>
              <span className="ais-sub">{field.hint}</span>
              {errors[field.key] && <span className="ais-error">{errors[field.key]}</span>}
            </label>
          ))}
        </div>
      </section>

      <section className="ais-section">
        <h3 className="ais-section-title">BOOKED slots</h3>
        <div className="ais-booked">
          <div className="ais-fields">
            {BOOKED_FIELDS.map(field => (
              <label key={field.key} className="ais-field" htmlFor={`ais-${field.key}`}>
                <span className="ais-label">{field.label}</span>
                <div className="ais-color-input">
                  <input
                    id={`ais-${field.key}`}
                    type="color"
                    value={draft[field.key]}
                    onChange={e => setField(field.key, e.target.value)}
                  />
                  <span className="ais-color-hex">{draft[field.key]}</span>
                </div>
                {errors[field.key] && <span className="ais-error">{errors[field.key]}</span>}
              </label>
            ))}
          </div>
          {/* Live sample of how a BOOKED cell will look in the images. */}
          <div className="ais-preview" style={{ background: draft.bookedBg, color: draft.bookedText }}>BOOKED</div>
        </div>
      </section>

      <button
        type="button"
        className="ais-reset"
        onClick={() => { setDraft(toDraft(AVAILABILITY_IMAGE_DEFAULT_SETTINGS)); setErrors({}); }}
      >
        Reset to defaults
      </button>
    </Modal>
  );
}

export default AvailabilityImageSettingsModal;
